import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageMeta from '@/components/feature/PageMeta';
import ProductCard from '@/components/feature/ProductCard';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories, products } from '@/mocks/products';
import { articles } from '@/mocks/articles';

const hotProducts = products.filter((p) => ['hot', 'popular', 'recommend', 'organic'].includes(p.badge || '')).slice(0, 4);

const faqItems = [
  { q: '爱威奶产品如何保证新鲜度？', a: '爱威奶所有产品从牧场到配送全程采用冷链管理，温度严格控制在2-6℃，确保产品从出厂到送达始终保持最佳新鲜状态。每批次产品均经过严格质检，可扫描包装二维码进行品质溯源。' },
  { q: '下单后多久能收到商品？', a: '全国大部分地区支持次日达服务，偏远地区2-3天送达。我们与顺丰冷链、京东物流等优质物流服务商合作，确保产品快速安全地送到您手中。当日18:00前下单，当日即可发出。' },
  { q: '产品不满意可以退换吗？', a: '我们提供7天无忧退换服务。如产品存在质量问题，请在签收后24小时内联系客服并提供相关凭证，我们将免费为您办理退换。非质量问题退换需产品未开封且包装完好。' },
  { q: '爱威奶有实体门店吗？', a: '爱威奶目前以线上销售为主，在全国主要城市设有仓储配送中心。您可以通过爱威奶在线官网直接下单，享受送货上门服务。部分城市社区便利店也可购买到爱威奶产品。' },
  { q: '如何成为爱威奶会员？有什么权益？', a: '注册爱威奶在线账号即可成为会员。会员享受专属优惠价、积分累积、生日礼券、新品优先体验等多项权益。累计消费还可升级会员等级，享受更多折扣。' },
];

const features = [
  { icon: 'ri-shield-check-fill', title: '正品保障', desc: '100%自有牧场奶源，全程可追溯，品质有保障' },
  { icon: 'ri-truck-fill', title: '冷链配送', desc: '全程冷链运输，确保新鲜到家，全国次日达' },
  { icon: 'ri-customer-service-2-fill', title: '贴心售后', desc: '7天无忧退换，专业客服团队，随时为您服务' },
  { icon: 'ri-medal-fill', title: '国际认证', desc: '通过GAP国际认证，品质达到国际先进水平' },
];

export default function Home() {
  return (
    <>
      <PageMeta
        title="爱威奶在线 - 官方正品购买平台 | 爱威奶在线购买"
        description="爱威奶在线为您提供最便捷的购买体验，汇聚正品爱威奶全系列产品，支持在线下单、快速配送、售后保障。无论您是初次了解还是忠实用户，爱威奶在线让您足不出户享受品质服务。"
        keywords="爱威奶在线,爱威奶在线购买,爱威奶正品官网,爱威奶在线下单,爱威奶产品系列,爱威奶优惠活动,爱威奶配送服务"
        canonicalUrl="https://www.aiweinai.com"
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[520px] md:h-[620px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20cream%20colored%20abstract%20flowing%20organic%20shapes%20with%20soft%20warm%20gradients%2C%20minimalist%20artistic%20background%20with%20smooth%20curves%2C%20dairy%20product%20inspiration%2C%20clean%20elegant%20design%2C%20no%20text%2C%20soft%20natural%20lighting%2C%20gentle%20pastel%20tones%2C%20abstract%20composition&width=1920&height=1080&seq=hero-aiweinai-01&orientation=landscape"
            alt="爱威奶在线 - 官方正品购买平台"
            title="爱威奶在线 - 官方正品购买平台"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/35"></div>
          <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-5 font-heading leading-tight max-w-3xl">
              每一滴都是<strong className="text-primary-300">大自然的馈赠</strong>
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-2xl mb-8 leading-relaxed">
              爱威奶在线官方正品购买平台，汇聚全系列产品。<br className="hidden sm:block" />
              在线下单、冷链配送、正品保障，品质牛奶一键到家。
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <Link
                to="/category"
                className="px-8 py-3.5 bg-primary-500 text-background-50 rounded-md text-base font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                探索全系列产品
              </Link>
              <Link
                to="/about"
                className="px-8 py-3.5 bg-white/15 text-white border border-white/30 rounded-md text-base font-medium hover:bg-white/25 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                了解爱威奶
              </Link>
            </div>
          </div>
        </section>

        {/* Features Bar */}
        <section className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {features.map((f, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 flex-shrink-0">
                    <i className={`${f.icon} text-lg`}></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{f.title}</p>
                    <p className="text-xs text-foreground-500">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
                全系列产品分类
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
                从纯牛奶到酸奶，从儿童专属到中老年高钙，总有一款适合您
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center p-5 md:p-6 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-200 hover:bg-primary-50/30 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300 mb-3">
                    <i className={`${cat.icon} text-xl md:text-2xl`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 text-center group-hover:text-primary-600 transition-colors">
                    {cat.name}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Products */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-10 gap-4">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-2">
                  精选热卖推荐
                </h2>
                <p className="text-sm text-foreground-500">众多消费者的信赖之选</p>
              </div>
              <Link
                to="/category"
                className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
              {hotProducts.map((product) => (
                <ProductCard key={product.id} {...product} />
              ))}
            </div>
          </div>
        </section>

        {/* Promo Banner */}
        <section className="relative py-16 md:py-20 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20golden%20cream%20abstract%20flowing%20waves%20background%20with%20soft%20gradients%2C%20minimalist%20elegant%20design%2C%20warm%20natural%20tones%2C%20smooth%20curves%2C%20dairy%20inspired%20abstract%20art%2C%20no%20text%2C%20clean%20composition&width=1920&height=600&seq=promo-01&orientation=landscape"
            alt="爱威奶夏日清凉季优惠活动"
            title="爱威奶夏日清凉季优惠活动"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 text-center">
            <span className="inline-block px-4 py-1.5 bg-accent-500 text-background-50 rounded-full text-sm font-semibold mb-4">
              限时活动
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-4 font-heading">
              夏日清凉季 · 全场满减活动进行中
            </h2>
            <p className="text-white/70 text-sm md:text-base mb-8 max-w-2xl mx-auto">
              全系列产品满199减30，新品酸奶买二送一，新人注册更有50元大礼包
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category"
                className="px-8 py-3 bg-accent-500 text-background-50 rounded-md text-sm font-semibold hover:bg-accent-600 transition-all whitespace-nowrap cursor-pointer"
              >
                立即抢购
              </Link>
              <Link
                to="/article/xia-ji-cu-xiao-huo-dong"
                className="px-8 py-3 bg-white/15 text-white border border-white/30 rounded-md text-sm font-semibold hover:bg-white/25 transition-all whitespace-nowrap cursor-pointer"
              >
                了解详情
              </Link>
            </div>
          </div>
        </section>

        {/* Articles */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-10 gap-4">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-2">
                  最新文章资讯
                </h2>
                <p className="text-sm text-foreground-500">了解更多牛奶知识与品牌动态</p>
              </div>
              <Link
                to="/articles"
                className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {articles.slice(0, 3).map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
                常见问题
              </h2>
              <p className="text-sm text-foreground-500">关于爱威奶在线购买，您可能想了解的问题</p>
            </div>
            <div className="space-y-3">
              {faqItems.map((item, idx) => (
                <details key={idx} className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100 transition-colors">
                    <span className="text-sm font-medium text-foreground-900 pr-6">{item.q}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-4">
              开启品质牛奶新体验
            </h2>
            <p className="text-sm md:text-base text-foreground-500 mb-8 max-w-xl mx-auto">
              新鲜直达，正品保障。爱威奶在线让每一杯牛奶都充满大自然的味道。
            </p>
            <Link
              to="/category"
              className="inline-flex px-8 py-3.5 bg-primary-500 text-background-50 rounded-md text-base font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              立即选购爱威奶产品
            </Link>
          </div>
        </section>
      </main>

      <Footer />

      {/* FAQPage Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqItems.map((item) => ({
            "@type": "Question",
            "name": item.q,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": item.a,
            },
          })),
        }),
      }} />

      {/* WebPage Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "爱威奶在线 - 官方正品购买平台",
          "description": "爱威奶在线为您提供最便捷的购买体验，汇聚正品爱威奶全系列产品，支持在线下单、快速配送、售后保障。",
          "url": "https://www.aiweinai.com",
          "isPartOf": { "@type": "WebSite", "name": "爱威奶在线", "url": "https://www.aiweinai.com" },
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [{
              "@type": "ListItem",
              "position": 1,
              "name": "首页",
              "item": "https://www.aiweinai.com",
            }],
          },
        }),
      }} />
    </>
  );
}